import { useEffect, useState } from "react";
import { Button, Divider, Grid, Header, Icon, Segment } from "semantic-ui-react";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { OrgUser } from "../models/OrgUser";
import { Project } from "../models/Project";
import { RegisterAssignment } from "../models/RegisterAssignment";
import { toastSuccess } from "../utils";
import AddRegisterAssignmentModal from "./AddRegisterAssignmentModal";
import MapWithBoundaries from "./MapWithBoundaries";
import ViewProjectModal from "./ViewProjectModal";
import { LicenceType } from "../models/globalTypes";
import { Boundary } from "../models/Boundary";
import { mapGeoJSONPolygonTypeToBoundary } from "../utils/boundaryUtils";
import { PermitType } from "../models/PermitType";
import { PermitRegister } from "../models/PermitRegister";
import PermitRegisterTypesGrid from "./PermitRegisterTypesGrid";
import AddPermitTypeToRegisterModal from "./AddPermitTypeToRegisterModal";
import ViewItem from "./common/ViewItem";

interface Props {
  showProjectBoundariesOnMap?: boolean;
  isPopup?: boolean;
  project?: Project;
  permitRegister?: PermitRegister;
  registerAssignments?: RegisterAssignment[];
  orgUsers?: OrgUser[];
  onClose: () => void;
  onAddRegisterAssignments: (orgUserIds: string[], permitTypeOrgRoleId: string) => void;
  addRegisterAssignmentsStatus: AsyncActionStatus;
  onDeleteRegisterAssignment: (id: string) => void;
  deleteRegisterAssignmentStatus: AsyncActionStatus;
  availablePermitTypes?: PermitType[];
  onAddPermitTypesToRegister: (permitTypeIds: string[]) => void;
  addPermitTypesToRegisterStatus: AsyncActionStatus;
}

const ProjectView = ({
  showProjectBoundariesOnMap,
  isPopup,
  project,
  permitRegister,
  registerAssignments,
  orgUsers,
  onClose,
  onAddRegisterAssignments,
  addRegisterAssignmentsStatus,
  onDeleteRegisterAssignment,
  deleteRegisterAssignmentStatus,
  availablePermitTypes,
  onAddPermitTypesToRegister,
  addPermitTypesToRegisterStatus,
}: Props) => {
  const [openViewProjectModal, setOpenViewProjectModal] = useState(false);
  const [openRegisterAssignmentModal, setOpenRegisterAssignmentModal] = useState(false);
  const [openAddTypeToRegisterModal, setOpenAddTypeToRegisterModal] = useState(false);
  const [permitTypeToEdit, setPermitTypeToEdit] = useState<PermitType | null>(null);

  useEffect(() => {
    if (deleteRegisterAssignmentStatus.successful) {
      toastSuccess("Assignment deleted successfully!");
    }
  }, [deleteRegisterAssignmentStatus.successful]);

  const handleModalClose = () => {
    setOpenViewProjectModal(false);
    setOpenRegisterAssignmentModal(false);
    setPermitTypeToEdit(null);
    setOpenAddTypeToRegisterModal(false);
    addRegisterAssignmentsStatus.reset();
    deleteRegisterAssignmentStatus.reset();
    addPermitTypesToRegisterStatus.reset();
  };

  const handleAddRegisterAssignmentClick = (permitTypeId: string) => {
    setPermitTypeToEdit(permitRegister?.permitTypes?.find((pt) => pt.id === permitTypeId) || null);
    setOpenRegisterAssignmentModal(true);
  };

  const handleRegisterAssignmentModalSubmit = (orgUserIds: string[], permitTypeOrgRoleId: string) => {
    onAddRegisterAssignments(orgUserIds, permitTypeOrgRoleId);
  };

  useEffect(() => {
    if (addRegisterAssignmentsStatus.successful) {
      toastSuccess("Assignments added successfully!");
      setOpenRegisterAssignmentModal(false);
      setPermitTypeToEdit(null);
    }
  }, [addRegisterAssignmentsStatus.successful]);

  const handleViewProjectPopup = () => {
    setOpenViewProjectModal(true);
  };

  const handleAddPermitTypeToRegisterSubmit = (permitTypeIds: string[]) => {
    onAddPermitTypesToRegister(permitTypeIds);
  };

  useEffect(() => {
    if (addPermitTypesToRegisterStatus.successful) {
      toastSuccess("Permit type(s) added to register successfully!");
      setOpenAddTypeToRegisterModal(false);
    }
  }, [addPermitTypesToRegisterStatus.successful]);

  return (
    <>
      {project && (
        <>
          <Segment attached="top" basic secondary compact>
            <Button
              circular
              icon="close"
              floated="right"
              style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
              onClick={onClose}
            ></Button>
            <>
              {!isPopup && (
                <>
                  <Button
                    circular
                    icon="arrow up"
                    floated="right"
                    style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
                    onClick={handleViewProjectPopup}
                  ></Button>
                </>
              )}
            </>
            {!isPopup && (
              <Button
                circular
                floated="right"
                style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
                onClick={() => setOpenAddTypeToRegisterModal(true)}
              >
                <Icon size="small" name="plus" />
                Add permit type
              </Button>
            )}
            <Header as="h4" style={{ display: "inline" }}>
              {permitRegister?.title}
            </Header>
          </Segment>

          <Segment attached color="green" style={{ borderTop: "3px !important" }}>
            <Grid>
              <Grid.Column width={showProjectBoundariesOnMap && isPopup ? "8" : "16"}>
                <Divider horizontal>Project details</Divider>
                <Grid columns="2">
                  <Grid.Row>
                    <Grid.Column>
                      <ViewItem header="Name" value={project.name} />
                    </Grid.Column>
                    <Grid.Column>
                      <ViewItem header="Number" value={project.number || "-"} />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <ViewItem header="Location" value={project.location} />
                    </Grid.Column>
                    <Grid.Column>
                      <ViewItem
                        header="Emergency Contact"
                        value={
                          project.emergencyContact?.name
                            ? `${project.emergencyContact.name} (${project.emergencyContact.phoneNumber})`
                            : "-"
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <ViewItem header="Team size limit" value={project.maxProjectAssignments || "-"} />
                    </Grid.Column>
                    <Grid.Column>
                      <ViewItem
                        header="Available team slots"
                        value={
                          project.maxProjectAssignments && project.standardLicenceAssignmentCount
                            ? project.maxProjectAssignments - project.standardLicenceAssignmentCount
                            : "-"
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row>
                    {permitRegister && (
                      <>
                        <Segment attached basic color="green" style={{ border: "0px" }}>
                          <Grid>
                            <Grid.Column width={"16"}>
                              <Header as="h4" style={{ display: "inline" }}>
                                Team assignments
                                <Header.Subheader>
                                  Active members:{" "}
                                  {registerAssignments?.filter(
                                    (a) => !a.isDisabled && a.orgUser.licence?.type === LicenceType.STANDARD,
                                  ).length || 0}
                                </Header.Subheader>
                              </Header>

                              <PermitRegisterTypesGrid
                                permitTypes={permitRegister.permitTypes}
                                registerAssignments={registerAssignments}
                                onAddRegisterAssignmentsClick={handleAddRegisterAssignmentClick}
                                onDeleteRegisterAssignment={onDeleteRegisterAssignment}
                              />
                            </Grid.Column>
                          </Grid>
                        </Segment>
                      </>
                    )}
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              {isPopup && showProjectBoundariesOnMap && project?.boundaries && (
                <Grid.Column width="8" style={{ padding: "0px", backgroundColor: "#f1f1f1" }}>
                  <MapWithBoundaries
                    items={[]}
                    withPopup={false}
                    boundaries={[mapGeoJSONPolygonTypeToBoundary(project.boundaries)] as Boundary[]}
                    height="600px"
                  />
                </Grid.Column>
              )}
            </Grid>
          </Segment>

          <ViewProjectModal
            showProjectBoundariesOnMap={showProjectBoundariesOnMap}
            open={openViewProjectModal}
            onClose={handleModalClose}
            project={project}
          ></ViewProjectModal>

          <AddRegisterAssignmentModal
            open={openRegisterAssignmentModal}
            onClose={handleModalClose}
            onSubmit={handleRegisterAssignmentModalSubmit}
            submitInProgress={addRegisterAssignmentsStatus.loading}
            submitErrors={addRegisterAssignmentsStatus.errors}
            availableOrgUsers={orgUsers?.filter(
              (u) =>
                u.hasActiveStandardLicence &&
                !registerAssignments
                  ?.filter((a) => a.permitTypeOrgRole.permitType.id === permitTypeToEdit?.id)
                  .some((a) => a.orgUser.id === u.id),
            )}
            availablePermitTypeOrgRoles={permitTypeToEdit?.roles?.filter(
              (r) => r.orgRole.licenceType !== LicenceType.VIEWER,
            )}
          />

          <AddPermitTypeToRegisterModal
            open={openAddTypeToRegisterModal}
            onClose={handleModalClose}
            onSubmit={handleAddPermitTypeToRegisterSubmit}
            submitInProgress={addPermitTypesToRegisterStatus.loading}
            submitErrors={addPermitTypesToRegisterStatus.errors}
            availablePermitTypes={availablePermitTypes}
          />
        </>
      )}{" "}
    </>
  );
};

export default ProjectView;
